// External Imports
import { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { MdCheck, MdClose } from "react-icons/md";

// Internal Imports
import { requestAdminAPI } from "../../api/posh-api";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";
import LinkPair from "../UI/LinkPair";
import Modal from "../UI/Modal";
import uploadFile from "../../util/upload-file";
import Heading from "../UI/Heading";
import { UtilityContext } from "../../context/util-context";
import { getISODate } from "../../util/format-date";

/** ========================================================================
 * * Add Application
 * Renders a form, which allows the user to add a partner's application.
 * ========================================================================== */

export default function AddApplication({ open, setOpen, onComplete }) {
  // Get Partner ID from URL
  const [searchParams] = useSearchParams();
  const partnerId = searchParams.get("id");

  // Load Context
  const { navigate } = useContext(UtilityContext);

  // Initialize States
  const [loading, setLoading] = useState(false);
  const [makes, setMakes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [item, setItem] = useState();
  const [error, setError] = useState(null);

  /* ======================= FETCH MAKES ======================= */
  const fetchMakes = async () => {
    try {
      setLoading(true);
      const res = await requestAdminAPI("fetch-all", {
        table: "make",
      });
      setMakes(res);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // ==================================== FILE VALIDATION
  /** Validates that a file is either a PDF or an image format */
  const validateFileType = (file, fieldName) => {
    if (!file) return null;
    
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp'
    ];
    
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    // Check MIME type
    if (!allowedTypes.includes(file.type)) {
      // Also check by extension as fallback
      if (!allowedExtensions.includes(fileExtension)) {
        return `Invalid file type for ${fieldName}. Only PDF and Image formats (JPG, PNG, WEBP) are allowed.`;
      }
    }
    
    return null;
  };

  // ==================================== VIN INPUT HANDLER
  /** This function is used to decode the VIN using POSH's API
   * and set the resulting vehicle information in the form */
  const vinInputHandler = async (vin) => {
    try {
      // Check if VIN is complete
      if (vin.length !== 17) return;
      // Send request to decode VIN
      const response = await requestAdminAPI("/decode-vin", { vin: vin });
      // Extract Results
      const vehicleInfo = response.vehicleInfo;

      // Check if brand is added
      if (
        !makes.find(
          (make) => make.name.toLowerCase() === vehicleInfo.make.toLowerCase()
        )
      )
        throw new Error("Vehicle Make not found. Please add the make first.");

      // Set Results
      Object.keys(vehicleInfo).forEach((key) => {
        if (key === "vin") return;
        if (!document.getElementById(key)) return;
        document.getElementById(key).value = vehicleInfo[key];
      });
    } catch (err) {
      console.error(err);
    }
  };

  /* ======================= ADD APPLICATION ======================= */
  const addApplicationHandler = async () => {
    try {
      setError(null);
      setLoading(true);
      const payload = {
        partnerId: partnerId,
        vin: document.getElementById("vin").value,
        make: document.getElementById("make").value,
        model: document.getElementById("model").value,
        year: document.getElementById("year").value,
        license: document.getElementById("license").value,
        licenseState: document.getElementById("licenseState").value,
        institution: document.getElementById("institution").value,
        financeAmount: document.getElementById("financeAmount").value,
        loanTerm: document.getElementById("loanTerm").value,
        monthlyPayment: document.getElementById("monthlyPayment").value,
        dueDate:
          document.getElementById("dueDate").value &&
          getISODate(document.getElementById("dueDate").value),
        registerationExp:
          document.getElementById("registerationExp").value &&
          getISODate(document.getElementById("registerationExp").value),
        insuranceExp:
          document.getElementById("insuranceExp").value &&
          getISODate(document.getElementById("insuranceExp").value),
      };

      // Get Files
      const files = {
        title: document.getElementById("title").files[0],
        registeration: document.getElementById("registeration").files[0],
        insurance: document.getElementById("insurance").files[0],
        billOfSale: document.getElementById("billOfSale").files[0],
        vehicleInspectionReport: document.getElementById(
          "vehicleInspectionReport"
        ).files[0],
        loanDocument: document.getElementById("loanDocument").files[0],
        powerOfAttorney: document.getElementById("powerOfAttorney").files[0],
        agreement: document.getElementById("agreement").files[0],
      };

      // Field name mapping for error messages
      const fieldNames = {
        title: "Title",
        registeration: "Registration",
        insurance: "Insurance",
        billOfSale: "Bill of Sale",
        vehicleInspectionReport: "Inspection Report",
        loanDocument: "Loan Document",
        powerOfAttorney: "Power of Attorney",
        agreement: "Vehicle Agreement",
      };

      // Validate all files before uploading
      for (const key in files) {
        if (files[key]) {
          const validationError = validateFileType(files[key], fieldNames[key]);
          if (validationError) {
            setError(validationError);
            setLoading(false);
            return;
          }
        }
      }

      // Upload Files
      for (const key in files) {
        if (files[key]) {
          const url = await uploadFile(
            `partners/${payload.vin}/${key}`,
            files[key],
            setUploading
          );
          setItem(key);
          payload[key] = url;
        }
      }

      // Send Request
      const applicationId = await requestAdminAPI(
        "manage-application",
        payload
      );

      navigate(`/applications/info?id=${applicationId}`);
      setOpen(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setError(err.message || "An error occurred while submitting the application.");
      setLoading(false);
    }
  };

  // Fetch Makes on Load
  useEffect(() => {
    fetchMakes();
  }, []);

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="grid gap-8 modal">
        <Heading
          title="Add Vehicle Application"
          text="Adding a vehicle application will notify both partner and admin. 
          Vehicles need to be onboarding separately before an application can be activated. VIN, year, make and model cannot be changed once submitted."
          isBackArrow={false}
        >
          <MdCheck className="icon" onClick={addApplicationHandler} />
          <MdClose className="icon" onClick={() => setOpen(false)} />
        </Heading>
        <div className="info">
          <DetailPair
            loading={loading}
            label="VIN"
            edit={true}
            inputOptions={{
              required: true,
              onChange: (e) => vinInputHandler(e.target.value),
              id: "vin",
            }}
          />
          <SelectPair
            loading={loading}
            label="Make"
            options={makes.map((make) => ({ id: make.id, name: make.name }))}
            edit={true}
            inputOptions={{
              required: true,
              id: "make",
            }}
          />
          <DetailPair
            loading={loading}
            label="Model"
            edit={true}
            inputOptions={{
              required: true,
              id: "model",
            }}
          />
          <DetailPair
            loading={loading}
            label="Year"
            edit={true}
            inputOptions={{
              required: true,
              type: "number",
              id: "year",
            }}
          />
          <DetailPair
            loading={loading}
            label="License"
            edit={true}
            inputOptions={{
              id: "license",
            }}
          />
          <DetailPair
            loading={loading}
            label="License State"
            edit={true}
            inputOptions={{
              id: "licenseState",
            }}
          />
          <LinkPair
            loading={loading}
            label="Title"
            edit={true}
            inputOptions={{
              type: "file",
              id: "title",
              accept: ".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp",
            }}
          />
          <LinkPair
            loading={loading}
            label="Registeration"
            edit={true}
            inputOptions={{
              type: "file",
              id: "registeration",
              accept: ".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp",
            }}
          />
          <DetailPair
            loading={loading}
            label="Registeration Expiration"
            edit={true}
            inputOptions={{
              type: "date",
              id: "registerationExp",
            }}
          />
          <LinkPair
            loading={loading}
            label="Insurance"
            edit={true}
            inputOptions={{
              type: "file",
              id: "insurance",
              accept: ".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp",
            }}
          />
          <DetailPair
            loading={loading}
            label="Insurance Expiration"
            edit={true}
            inputOptions={{
              type: "date",
              id: "insuranceExp",
            }}
          />
          <LinkPair
            loading={loading}
            label="Bill of Sale"
            edit={true}
            inputOptions={{
              type: "file",
              id: "billOfSale",
              accept: ".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp",
            }}
          />
          <LinkPair
            loading={loading}
            label="Inspection Report"
            edit={true}
            inputOptions={{
              type: "file",
              id: "vehicleInspectionReport",
              accept: ".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp",
            }}
          />
          <LinkPair
            loading={loading}
            label="Loan Document"
            edit={true}
            inputOptions={{
              type: "file",
              id: "loanDocument",
              accept: ".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp",
            }}
          />
          <LinkPair
            loading={loading}
            label="Power of Attorney"
            edit={true}
            inputOptions={{
              type: "file",
              id: "powerOfAttorney",
              accept: ".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp",
            }}
          />
          <LinkPair
            loading={loading}
            label="Vehicle Agreement"
            edit={true}
            inputOptions={{
              type: "file",
              id: "agreement",
              accept: ".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp",
            }}
          />
          <DetailPair
            loading={loading}
            label="Financing Institution"
            edit={true}
            inputOptions={{
              id: "institution",
            }}
          />
          <DetailPair
            loading={loading}
            label="Finance Amount"
            edit={true}
            inputOptions={{
              id: "financeAmount",
            }}
          />
          <DetailPair
            loading={loading}
            label="Term (Months)"
            edit={true}
            inputOptions={{
              id: "loanTerm",
            }}
          />
          <DetailPair
            loading={loading}
            label="Monthly Payment"
            edit={true}
            inputOptions={{
              id: "monthlyPayment",
            }}
          />
          <DetailPair
            loading={loading}
            label="Due Date"
            edit={true}
            inputOptions={{
              type: "date",
              id: "dueDate",
            }}
          />
        </div>

        {uploading && (
          <p className="text-stone-400">
            Uploading {item}: {uploading}%
          </p>
        )}
        {error && (
          <div className="p-4 bg-red-900/20 border border-red-500 rounded text-red-400">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
